/**
 * What changed between two saved templates, said in words.
 *
 * A revision history is only useful if it can answer "what is different about
 * this one" without asking anybody to read a template. A structural diff of the
 * JSON answers a different question — which keys moved — and it answers it in
 * the vocabulary of the format: `body[2].children[0].props.columns`. This speaks
 * the vocabulary of the designer instead: the table in “Summary”, the page
 * margins, the Heading 1 style.
 *
 * Three decisions shape everything below.
 *
 * **Nodes are matched by id, never by position.** Every node already carries a
 * stable id, and matching by position is how inserting one section at the top
 * becomes "changed every section in the document". A subtree added or removed
 * is one change, not one per descendant — a person who deleted a section
 * deleted a section.
 *
 * **Expression syntax never reaches the words.** A host may hide expressions
 * entirely (`expressions: false`), and a revision list that printed
 * `sum(invoice.lineItems.amount) | currency` would put back exactly what that
 * flag took away. Bindings are named with `describeExpression`, the same
 * function the canvas chips use, and anything that still would not read as
 * words is called "a calculated value".
 *
 * **It is pure.** No DOM, no registry required, deterministic output — the same
 * two templates always produce the same list in the same order, so a host can
 * store the summary beside a revision and trust it later.
 */
import type { BlockRegistry } from "../registry/registry.js";
import type { InspectorField } from "../registry/block-definition.js";
import type { DataSource } from "../types/data.js";
import type { Node, PageSettings, ReportTemplate } from "../types/template.js";
import { createSchemaScope, type SchemaScope } from "../expr/typecheck.js";
import { describeExpression } from "../resolve/describe.js";
import { isRichTextDoc, richTextToPlain } from "../richtext/model.js";
import { humanize, textStyleLabel } from "../theme/tokens.js";

export type ChangeKind = "added" | "removed" | "changed" | "moved";
export type ChangeArea = "document" | "page" | "styles" | "header" | "footer" | "body";

export interface TemplateChange {
  kind: ChangeKind;
  area: ChangeArea;
  /** The node's id, when the change is to a node. */
  nodeId?: string;
  /** What changed, named for a person: "Table", "Top margin", "Heading 1 style". */
  subject: string;
  /** One sentence: `Added a table to “Summary”`, `Changed the page margins`. */
  summary: string;
  /** Display strings, only when a single simple value changed. ≤ 80 chars each, truncated with "…". */
  before?: string;
  after?: string;
}

export interface TemplateDiff {
  changes: TemplateChange[];
  counts: Record<ChangeKind, number>;
  /** Changes beyond `maxChanges` that were counted but not listed. */
  truncated: number;
  identical: boolean;
}

export interface DiffOptions {
  /** Block definitions, for naming block types and their properties. */
  registry?: BlockRegistry;
  /** Default 200. */
  maxChanges?: number;
  /**
   * The data sources the template binds to, so a binding is named by its field's
   * own label ("Invoice number") rather than a humanised key ("Number").
   * Optional: without them the names are still words, just plainer ones.
   */
  dataSources?: DataSource[];
}

const DISPLAY_LIMIT = 80;
const SUMMARY_LIMIT = 120;

const AREA_RANK: Record<ChangeArea, number> = { document: 0, page: 1, styles: 2, header: 3, body: 4, footer: 5 };

/** A change with the position it sorts by, which never leaves this module. */
interface Placed extends TemplateChange {
  rank: number;
}

export function diffTemplates(before: ReportTemplate, after: ReportTemplate, options: DiffOptions = {}): TemplateDiff {
  const ctx: Context = {
    registry: options.registry,
    scope: createSchemaScope(options.dataSources ?? [])
  };

  const placed: Placed[] = [
    ...documentChanges(before, after, ctx),
    ...pageChanges(before.page, after.page),
    ...styleChanges(before, after, ctx),
    ...nodeChanges(before, after, ctx)
  ];

  // Stable: equal ranks keep the order they were produced in, which is already
  // document order within each group.
  const ordered = placed
    .map((c, i) => ({ c, i }))
    .sort((a, b) => AREA_RANK[a.c.area] - AREA_RANK[b.c.area] || a.c.rank - b.c.rank || a.i - b.i)
    .map(({ c }) => strip(c));

  const counts: Record<ChangeKind, number> = { added: 0, removed: 0, changed: 0, moved: 0 };
  for (const change of ordered) counts[change.kind]++;
  const max = Math.max(0, options.maxChanges ?? 200);

  return {
    changes: ordered.slice(0, max),
    counts,
    truncated: Math.max(0, ordered.length - max),
    identical: ordered.length === 0
  };
}

/**
 * One line for a list of revisions.
 *
 * A single change is worth naming outright; several are counted, because a list
 * row that tried to name seven changes would be truncated into saying none of
 * them properly.
 */
export function summariseDiff(diff: TemplateDiff): string {
  const total = diff.counts.added + diff.counts.removed + diff.counts.changed + diff.counts.moved;
  if (total === 0) return "No changes";
  if (total === 1) {
    const only = diff.changes[0];
    return clip(only ? `1 change: ${only.summary}` : "1 change", SUMMARY_LIMIT);
  }
  const parts = (["added", "changed", "moved", "removed"] as const).filter((k) => diff.counts[k] > 0).map((k) => `${diff.counts[k]} ${k}`);
  return clip(`${total} changes: ${parts.join(", ")}`, SUMMARY_LIMIT);
}

/* -------------------------------------------------------------- context */

interface Context {
  registry: BlockRegistry | undefined;
  scope: SchemaScope;
}

function strip(change: Placed): TemplateChange {
  const { rank, ...rest } = change;
  void rank;
  const out: TemplateChange = { kind: rest.kind, area: rest.area, subject: rest.subject, summary: rest.summary };
  if (rest.nodeId !== undefined) out.nodeId = rest.nodeId;
  if (rest.before !== undefined) out.before = clip(rest.before, DISPLAY_LIMIT);
  if (rest.after !== undefined) out.after = clip(rest.after, DISPLAY_LIMIT);
  return out;
}

/* ------------------------------------------------------------- document */

function documentChanges(before: ReportTemplate, after: ReportTemplate, ctx: Context): Placed[] {
  void ctx;
  const out: Placed[] = [];
  if (before.name !== after.name) {
    out.push({ kind: "changed", area: "document", subject: "Design name", summary: "Renamed the design", before: before.name, after: after.name, rank: 0 });
  }
  // `dataSources` in settings is the host's bookkeeping about which sources a
  // template was built against, and nothing a person chose — so it is not a change
  // worth listing.
  const settings: Array<[keyof NonNullable<ReportTemplate["settings"]>, string, string]> = [
    ["locale", "Language and region", "Changed the language and region"],
    ["currency", "Currency", "Changed the currency"],
    ["timeZone", "Time zone", "Changed the time zone"]
  ];
  settings.forEach(([key, subject, summary], i) => {
    const a = before.settings?.[key];
    const b = after.settings?.[key];
    if (a === b) return;
    out.push({ kind: "changed", area: "document", subject, summary, before: a === undefined ? "Default" : String(a), after: b === undefined ? "Default" : String(b), rank: i + 1 });
  });
  return out;
}

/* ----------------------------------------------------------------- page */

function pageChanges(before: PageSettings, after: PageSettings): Placed[] {
  const out: Placed[] = [];
  let rank = 0;
  const push = (subject: string, summary: string, a?: string, b?: string) => {
    out.push({ kind: "changed", area: "page", subject, summary, ...(a !== undefined ? { before: a } : {}), ...(b !== undefined ? { after: b } : {}), rank: rank++ });
  };

  if (!same(before.size, after.size)) push("Page size", "Changed the page size", pageSize(before.size), pageSize(after.size));
  if (before.orientation !== after.orientation) push("Page orientation", "Changed the page orientation", humanize(before.orientation), humanize(after.orientation));
  // One change for all four, because nobody thinks of a margin change as four
  // edits — and four rows saying "Top margin", "Right margin"… would bury the
  // one that matters.
  if (!same(before.margins, after.margins)) push("Page margins", "Changed the page margins", margins(before.margins), margins(after.margins));

  for (const region of ["header", "footer"] as const) {
    const a = before[region];
    const b = after[region];
    const name = region === "header" ? "Header" : "Footer";
    if (a?.height !== b?.height) push(`${name} height`, `Changed the ${region} height`, regionHeight(a?.height), regionHeight(b?.height));
    if (Boolean(a?.differentFirstPage) !== Boolean(b?.differentFirstPage)) {
      push(
        `Different first-page ${region}`,
        b?.differentFirstPage ? `Turned on a different ${region} for the first page` : `Turned off the different ${region} for the first page`,
        onOff(a?.differentFirstPage),
        onOff(b?.differentFirstPage)
      );
    }
  }

  if (before.background !== after.background) push("Page background", "Changed the page background", colour(before.background), colour(after.background));
  if (before.minPages !== after.minPages) {
    push("Empty pages shown", "Changed how many pages an empty document shows", String(before.minPages ?? 1), String(after.minPages ?? 1));
  }

  // Anything a newer schema added that this does not know by name still counts,
  // rather than disappearing from the history because nobody wrote a sentence for it.
  const known = new Set(["size", "orientation", "margins", "header", "footer", "background", "minPages"]);
  const extra = keysOf(before, after).filter((k) => !known.has(k) && !same((before as unknown as Record<string, unknown>)[k], (after as unknown as Record<string, unknown>)[k]));
  if (extra.length) push("Page settings", "Changed the page settings");
  return out;
}

function pageSize(size: PageSettings["size"]): string {
  return typeof size === "string" ? size : `${size.width} × ${size.height} mm`;
}

function margins(m: PageSettings["margins"]): string {
  return [m.top, m.right, m.bottom, m.left].map((v) => `${v} mm`).join(" · ");
}

function regionHeight(height: number | undefined): string {
  return !height ? "None" : `${height} mm`;
}

/* --------------------------------------------------------------- styles */

/** What each style property is called, where a person reads it. */
const TEXT_STYLE_WORDS: Record<string, string> = {
  fontFamily: "font",
  fontSize: "size",
  fontWeight: "weight",
  fontStyle: "slant",
  lineHeight: "line height",
  letterSpacing: "letter spacing",
  textTransform: "capitalisation",
  color: "colour",
  marginTop: "space above",
  marginBottom: "space below",
  keepWithNext: "keep with next"
};

const TABLE_WORDS: Record<string, string> = {
  fontSize: "text size",
  headerBackground: "header background",
  headerColor: "header text colour",
  headerFontWeight: "header weight",
  borderColor: "border colour",
  borderWidth: "border width",
  borders: "lines",
  zebraBackground: "stripe colour",
  cellPaddingX: "cell padding",
  cellPaddingY: "cell padding"
};

const CARD_WORDS: Record<string, string> = {
  background: "background",
  borderColor: "border colour",
  borderWidth: "border width",
  borderRadius: "corner radius",
  padding: "padding",
  shadow: "shadow"
};

const LINK_WORDS: Record<string, string> = { color: "colour", underline: "underline" };

const SPACING_WORDS: Record<string, string> = {
  blockGap: "space between blocks",
  sectionPadding: "section padding",
  columnGap: "space between columns"
};

function styleChanges(before: ReportTemplate, after: ReportTemplate, ctx: Context): Placed[] {
  void ctx;
  const out: Placed[] = [];
  let rank = 0;
  const a = before.styles ?? ({} as ReportTemplate["styles"]);
  const b = after.styles ?? ({} as ReportTemplate["styles"]);

  // Text styles, in the order the newer template lists them and then any that
  // only the older one had — so a removed style sorts after the ones that stayed.
  const beforeText = (a.text ?? {}) as Record<string, Record<string, unknown>>;
  const afterText = (b.text ?? {}) as Record<string, Record<string, unknown>>;
  const ids = [...Object.keys(afterText), ...Object.keys(beforeText).filter((id) => !(id in afterText))];
  for (const id of ids) {
    const label = textStyleLabel(id);
    const subject = `${label} style`;
    if (!(id in beforeText)) {
      out.push({ kind: "added", area: "styles", subject, summary: `Added the ${label} style`, rank: rank++ });
    } else if (!(id in afterText)) {
      out.push({ kind: "removed", area: "styles", subject, summary: `Removed the ${label} style`, rank: rank++ });
    } else {
      const change = groupChange(beforeText[id]!, afterText[id]!, TEXT_STYLE_WORDS, (words) => `Changed the ${words} of the ${label} style`, subject);
      if (change) out.push({ ...change, rank: rank++ });
    }
  }

  const groups: Array<[keyof ReportTemplate["styles"], Record<string, string>, string, (words: string) => string]> = [
    ["table", TABLE_WORDS, "Table style", (w) => `Changed the ${w} of tables`],
    ["card", CARD_WORDS, "Card style", (w) => `Changed the ${w} of cards`],
    ["link", LINK_WORDS, "Link style", (w) => `Changed the ${w} of links`],
    ["spacing", SPACING_WORDS, "Spacing", (w) => `Changed the ${w}`]
  ];
  for (const [key, words, subject, sentence] of groups) {
    const change = groupChange(
      ((a as unknown as Record<string, unknown>)[key] ?? {}) as Record<string, unknown>,
      ((b as unknown as Record<string, unknown>)[key] ?? {}) as Record<string, unknown>,
      words,
      sentence,
      subject
    );
    if (change) out.push({ ...change, rank: rank++ });
  }

  const known = new Set(["text", "table", "card", "link", "spacing"]);
  const extra = keysOf(a, b).filter((k) => !known.has(k) && !same((a as unknown as Record<string, unknown>)[k], (b as unknown as Record<string, unknown>)[k]));
  if (extra.length) out.push({ kind: "changed", area: "styles", subject: "Document styles", summary: "Changed the document styles", rank: rank++ });
  return out;
}

/** One change for a whole group of style properties, naming what moved in it. */
function groupChange(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  words: Record<string, string>,
  sentence: (words: string) => string,
  subject: string
): Omit<Placed, "rank"> | null {
  const changed = keysOf(before, after).filter((k) => !same(before[k], after[k]));
  if (!changed.length) return null;
  const names = unique(changed.map((k) => words[k] ?? lowerFirst(humanize(k))));
  const change: Omit<Placed, "rank"> = { kind: "changed", area: "styles", subject, summary: sentence(list(names)) };
  if (changed.length === 1) {
    const key = changed[0]!;
    change.before = styleValue(key, before[key]);
    change.after = styleValue(key, after[key]);
  }
  return change;
}

function styleValue(key: string, value: unknown): string {
  if (value === undefined || value === null) return "Default";
  if (typeof value === "boolean") return onOff(value);
  if (typeof value === "number") return /line|weight/i.test(key) ? String(value) : `${value} px`;
  if (typeof value === "string") return /colou?r|background/i.test(key) ? colour(value) : value.startsWith("$") ? token(value) : humanize(value);
  return "Custom";
}

/* ---------------------------------------------------------------- nodes */

type Region = "header" | "body" | "footer";

interface Entry {
  node: Node;
  /** The node that holds it, or null at the top of a region list. */
  parentId: string | null;
  /** Distinguishes the five top-level lists from each other. */
  parentKey: string;
  area: Region;
  /** Position in a depth-first walk of the whole template. */
  order: number;
}

interface Indexed {
  byId: Map<string, Entry>;
  ordered: Entry[];
  /** Children in order, keyed by `parentKey`. */
  children: Map<string, string[]>;
}

function indexTemplate(template: ReportTemplate): Indexed {
  const byId = new Map<string, Entry>();
  const ordered: Entry[] = [];
  const children = new Map<string, string[]>();
  const lists: Array<[Region, string, Node[] | undefined]> = [
    ["header", "header:default", template.header?.default],
    ["header", "header:firstPage", template.header?.firstPage],
    ["body", "body", template.body],
    ["footer", "footer:default", template.footer?.default],
    ["footer", "footer:firstPage", template.footer?.firstPage]
  ];
  const walk = (nodes: Node[], area: Region, parentKey: string, parentId: string | null) => {
    const ids: string[] = [];
    for (const node of nodes) {
      // A duplicate id would make "which one changed" unanswerable; the first
      // one wins, which is also what the editor's own lookup does.
      if (byId.has(node.id)) continue;
      const entry: Entry = { node, parentId, parentKey, area, order: ordered.length };
      byId.set(node.id, entry);
      ordered.push(entry);
      ids.push(node.id);
      if (node.children?.length) walk(node.children, area, node.id, node.id);
    }
    children.set(parentKey, ids);
  };
  for (const [area, key, nodes] of lists) if (nodes) walk(nodes, area, key, null);
  return { byId, ordered, children };
}

function nodeChanges(before: ReportTemplate, after: ReportTemplate, ctx: Context): Placed[] {
  const a = indexTemplate(before);
  const b = indexTemplate(after);
  const out: Placed[] = [];

  /*
   * Where a removed node sorts.
   *
   * It no longer has a position in the newer template, so it takes the position
   * just after the nearest earlier node that survived — which puts "Removed the
   * chart" next to the things that were beside it rather than at the end of the
   * list.
   */
  const removedRank = (entry: Entry): number => {
    for (let i = entry.order - 1; i >= 0; i--) {
      const survivor = b.byId.get(a.ordered[i]!.node.id);
      if (survivor) return survivor.order + 0.5;
    }
    return -0.5;
  };

  for (const entry of a.ordered) {
    const id = entry.node.id;
    if (b.byId.has(id)) continue;
    // Inside something that was removed as well: that removal already says it.
    if (entry.parentId && !b.byId.has(entry.parentId)) continue;
    out.push({
      kind: "removed",
      area: entry.area,
      nodeId: id,
      subject: subjectOf(entry, a, ctx),
      summary: `Removed ${refOf(entry, a, ctx, "definite")}${place(entry, a, "from", ctx)}`,
      rank: removedRank(entry)
    });
  }

  // Which surviving nodes changed their order among the siblings both versions share.
  const reordered = new Set<string>();
  for (const [key, afterIds] of b.children) {
    const beforeIds = a.children.get(key);
    if (!beforeIds) continue;
    const shared = beforeIds.filter((id) => afterIds.includes(id));
    if (shared.length < 2) continue;
    const positions = shared.map((id) => afterIds.indexOf(id));
    const keep = longestIncreasing(positions);
    shared.forEach((id, i) => {
      if (!keep.has(i)) reordered.add(id);
    });
  }

  for (const entry of b.ordered) {
    const id = entry.node.id;
    const was = a.byId.get(id);
    if (!was) {
      if (entry.parentId && !a.byId.has(entry.parentId)) continue;
      out.push({
        kind: "added",
        area: entry.area,
        nodeId: id,
        subject: subjectOf(entry, b, ctx),
        summary: `Added ${refOf(entry, b, ctx, "indefinite")}${place(entry, b, "to", ctx)}`,
        rank: entry.order
      });
      continue;
    }

    if (was.parentKey !== entry.parentKey) {
      const from = place(was, a, "from", ctx);
      const to = place(entry, b, "to", ctx);
      const within = place(entry, b, "within", ctx);
      out.push({
        kind: "moved",
        area: entry.area,
        nodeId: id,
        subject: subjectOf(entry, b, ctx),
        // Between two columns of the same section is still "within" it: naming the
        // same section twice would read as a mistake.
        summary: `Moved ${refOf(entry, b, ctx, "definite")}${from.replace(/^ from /, "") === to.replace(/^ to /, "") ? within : `${from}${to}`}`,
        rank: entry.order
      });
    } else if (reordered.has(id)) {
      out.push({
        kind: "moved",
        area: entry.area,
        nodeId: id,
        subject: subjectOf(entry, b, ctx),
        summary: `Moved ${refOf(entry, b, ctx, "definite")}${place(entry, b, "within", ctx)}`,
        rank: entry.order
      });
    }

    const changed = nodeEdit(was, a, entry, b, ctx);
    if (changed) out.push({ ...changed, rank: entry.order + 0.25 });
  }

  return out;
}

/** The change to a node's own settings, ignoring its children and its lock. */
function nodeEdit(was: Entry, wasIndex: Indexed, now: Entry, nowIndex: Indexed, ctx: Context): Placed | null {
  const x = was.node;
  const y = now.node;
  const propKeys = keysOf(x.props ?? {}, y.props ?? {}).filter((k) => !same(x.props?.[k], y.props?.[k]));
  const typeChanged = x.type !== y.type;
  const styleChanged = !same(x.style, y.style);
  const visibilityChanged = !same(x.visibleWhen, y.visibleWhen);
  const flowChanged = !same(x.flow, y.flow);
  const nameChanged = (x.name ?? "") !== (y.name ?? "");
  if (!propKeys.length && !typeChanged && !styleChanged && !visibilityChanged && !flowChanged && !nameChanged) return null;

  const ref = refOf(now, nowIndex, ctx, "definite");
  const where = place(now, nowIndex, "in", ctx);
  const base = { kind: "changed" as const, area: now.area, nodeId: y.id, subject: subjectOf(now, nowIndex, ctx), rank: 0 };
  const aspects = propKeys.length + Number(styleChanged) + Number(visibilityChanged) + Number(flowChanged) + Number(nameChanged) + Number(typeChanged);

  if (aspects === 1 && nameChanged) {
    // Renaming changes what the node is called, so the sentence names the old one.
    return { ...base, summary: `Renamed ${refOf(was, wasIndex, ctx, "definite")}${place(was, wasIndex, "in", ctx)}`, before: x.name ?? "", after: y.name ?? "" };
  }
  if (aspects === 1 && styleChanged) return { ...base, summary: `Changed the look of ${ref}${where}` };
  if (aspects === 1 && visibilityChanged) return { ...base, summary: `Changed when ${ref}${where} appears` };
  if (aspects === 1 && flowChanged) return { ...base, summary: `Changed how ${ref}${where} breaks across pages` };

  const names = unique([
    ...(typeChanged ? ["type"] : []),
    ...propKeys.map((k) => propLabel(y.type, k, ctx)),
    ...(styleChanged ? ["look"] : []),
    ...(visibilityChanged ? ["visibility"] : []),
    ...(flowChanged ? ["page breaks"] : []),
    ...(nameChanged ? ["name"] : [])
  ]);
  const summary = names.length > 3 ? `Changed ${names.length} settings of ${ref}${where}` : `Changed the ${list(names)} of ${ref}${where}`;
  const change: Placed = { ...base, summary };

  if (aspects === 1 && propKeys.length === 1) {
    const key = propKeys[0]!;
    const from = simpleValue(x.type, key, x.props?.[key], ctx);
    const to = simpleValue(y.type, key, y.props?.[key], ctx);
    if (from !== null && to !== null) {
      change.before = from;
      change.after = to;
    }
  }
  return change;
}

/* ---------------------------------------------------------- node naming */

/** Structural types get their own nouns: the registry calls a row "Columns". */
const STRUCTURE_NOUNS: Record<string, string> = { section: "section", row: "row of columns", column: "column" };

function blockName(type: string, ctx: Context): string {
  if (STRUCTURE_NOUNS[type]) return humanize(STRUCTURE_NOUNS[type]!);
  const name = ctx.registry?.get(type)?.name;
  return name && name.trim() ? name.trim() : humanize(type.replace(/[-_.]+/g, " "));
}

function subjectOf(entry: Entry, index: Indexed, ctx: Context): string {
  const node = entry.node;
  if (node.name?.trim()) return node.name.trim();
  if (node.type === "section") {
    const label = sectionLabel(entry, index, ctx);
    return label.quoted ? `Section “${label.text}”` : humanize(label.text);
  }
  return blockName(node.type, ctx);
}

function refOf(entry: Entry, index: Indexed, ctx: Context, form: "definite" | "indefinite"): string {
  const node = entry.node;
  if (node.type === "section") {
    const label = sectionLabel(entry, index, ctx);
    return label.quoted ? `the section “${label.text}”` : label.text;
  }
  if (node.name?.trim()) return `“${node.name.trim()}”`;
  const noun = lowerFirst(blockName(node.type, ctx));
  if (form === "definite") return `the ${noun}`;
  return `${/^[aeiou]/i.test(noun) ? "an" : "a"} ${noun}`;
}

/**
 * What a section is called: its own name, else the text of its first heading,
 * else its position — "section 2". A section with neither a name nor a heading
 * is usually a band of figures, and its position is the only thing a person
 * could find it by.
 */
function sectionLabel(entry: Entry, index: Indexed, ctx: Context): { text: string; quoted: boolean } {
  const node = entry.node;
  if (node.name?.trim()) return { text: node.name.trim(), quoted: true };
  const heading = firstHeading(node, ctx);
  if (heading) return { text: heading, quoted: true };
  const siblings = index.children.get(entry.parentKey) ?? [];
  const sections = siblings.filter((id) => index.byId.get(id)?.node.type === "section");
  return { text: `section ${sections.indexOf(node.id) + 1}`, quoted: false };
}

function firstHeading(node: Node, ctx: Context): string | null {
  for (const child of node.children ?? []) {
    if (child.type === "heading") {
      const text = child.props?.text;
      if (typeof text === "string" && text.trim()) return clip(readableText(text, ctx).trim(), 60);
      if (isRichTextDoc(text)) return clip(richTextToPlain(text, (e) => expressionLabel(e, ctx)).trim(), 60);
    }
    const nested = firstHeading(child, ctx);
    if (nested) return nested;
  }
  return null;
}

/**
 * Where a node is: ` in “Summary”`, ` to section 2`, ` from the header`.
 *
 * The nearest section above it names the place, because a section is the thing
 * a person scrolls to. With no section above it — a block straight in the header
 * or footer — the region does. A section at the top of the body is its own place,
 * and says nothing.
 */
function place(entry: Entry, index: Indexed, preposition: "in" | "to" | "from" | "within", ctx: Context): string {
  let cursor = entry.parentId ? index.byId.get(entry.parentId) : undefined;
  while (cursor) {
    if (cursor.node.type === "section") {
      const label = sectionLabel(cursor, index, ctx);
      return ` ${preposition} ${label.quoted ? `“${label.text}”` : label.text}`;
    }
    cursor = cursor.parentId ? index.byId.get(cursor.parentId) : undefined;
  }
  if (entry.area === "header") return ` ${preposition} the header`;
  if (entry.area === "footer") return ` ${preposition} the footer`;
  return preposition === "within" ? " within the document" : "";
}

/* ------------------------------------------------------------ properties */

function propLabel(type: string, prop: string, ctx: Context): string {
  const def = ctx.registry?.get(type);
  const find = (fields: InspectorField[]): string | null => {
    for (const field of fields) if (field.prop === prop || field.prop.startsWith(`${prop}.`)) return field.label;
    return null;
  };
  for (const section of def?.inspector ?? []) {
    const label = find(section.fields);
    if (label) return lowerFirst(label);
  }
  return lowerFirst(humanize(prop));
}

/**
 * A property's value as a person would read it, or null when it is not one value.
 *
 * Only strings, numbers, booleans and rich text count: a table's columns or a
 * chart's series changing is described by name, and a before/after pair of two
 * JSON arrays would say nothing.
 */
function simpleValue(type: string, prop: string, value: unknown, ctx: Context): string | null {
  if (value === undefined || value === null) return "None";
  if (typeof value === "boolean") return onOff(value);
  if (typeof value === "number") return String(value);
  if (isRichTextDoc(value)) return oneLine(richTextToPlain(value, (e) => expressionLabel(e, ctx)));
  if (typeof value !== "string") return null;

  const kind = ctx.registry?.bindingsOf(type).find((b) => b.prop === prop)?.kind;
  if (kind === "value" || kind === "collection") return expressionLabel(value, ctx);
  if (kind === "condition" || kind === "list") return null;
  if (!kind && looksLikeExpression(value)) return expressionLabel(value, ctx);
  return oneLine(readableText(value, ctx));
}

/** `{{ tokens }}` in a text prop, each replaced by what it names. */
function readableText(text: string, ctx: Context): string {
  return text.replace(/\{\{([^}]*)\}\}/g, (_, expr: string) => expressionLabel(expr.trim(), ctx));
}

/**
 * Without a registry there is no telling a binding prop from a text prop, so the
 * unmistakable shapes are treated as expressions: a pipe, or a function call.
 * A bare dotted word is left alone — "broadpaper.com" is text far more often
 * than it is a path.
 */
function looksLikeExpression(value: string): boolean {
  return /\|\s*[A-Za-z_]\w*/.test(value) || /^\s*[A-Za-z_]\w*\s*\(/.test(value);
}

function expressionLabel(expr: string, ctx: Context): string {
  if (!expr.trim()) return "nothing";
  const { label } = describeExpression(expr, ctx.scope);
  // `describeExpression` hands back the source itself when it finds nothing to
  // name — a literal, or something it could not parse. Words survive; syntax
  // does not.
  if (label === expr && /[^\w\s'’-]/.test(expr)) return "a calculated value";
  return label;
}

/* ---------------------------------------------------------------- words */

function colour(value: string | undefined): string {
  if (!value) return "None";
  return value.startsWith("$") ? token(value) : value;
}

/** `$colors.primary` as "Theme primary". */
function token(value: string): string {
  const last = value.slice(1).split(".").pop() ?? value;
  return `Theme ${lowerFirst(humanize(last))}`;
}

function onOff(value: boolean | undefined): string {
  return value ? "On" : "Off";
}

function list(words: string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** Lower-cases a leading capital, but not an acronym: "Table" → "table", "KPI" stays. */
function lowerFirst(text: string): string {
  if (text.length > 1 && text[1] === text[1]!.toUpperCase() && /[A-Z]/.test(text[1]!)) return text;
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function oneLine(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function clip(text: string, limit: number): string {
  return text.length <= limit ? text : `${text.slice(0, limit - 1).trimEnd()}…`;
}

function unique<T>(items: T[]): T[] {
  return [...new Set(items)];
}

/* ------------------------------------------------------------- equality */

function keysOf(a: unknown, b: unknown): string[] {
  const keys = new Set<string>();
  for (const o of [a, b]) if (o && typeof o === "object") for (const k of Object.keys(o)) keys.add(k);
  return [...keys];
}

/**
 * Deep equality that ignores key order and treats a missing key as undefined.
 *
 * A template that went through a JSON round trip, or through a store that
 * rebuilt its objects, is the same template; comparing references or serialised
 * strings would call it changed.
 */
function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const other = b as unknown[];
    return a.length === other.length && a.every((v, i) => same(v, other[i]));
  }
  const x = a as Record<string, unknown>;
  const y = b as Record<string, unknown>;
  return keysOf(x, y).every((k) => same(x[k], y[k]));
}

/**
 * Indexes of a longest strictly increasing subsequence.
 *
 * The siblings in that subsequence kept their relative order; everything else is
 * what moved. Without it, inserting one block at the top would shift every later
 * sibling's index and each would be reported as moved.
 */
function longestIncreasing(values: number[]): Set<number> {
  const tails: number[] = [];
  const previous: number[] = new Array(values.length).fill(-1);
  for (let i = 0; i < values.length; i++) {
    let lo = 0;
    let hi = tails.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (values[tails[mid]!]! < values[i]!) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0) previous[i] = tails[lo - 1]!;
    tails[lo] = i;
  }
  const keep = new Set<number>();
  let cursor = tails.length ? tails[tails.length - 1]! : -1;
  while (cursor >= 0) {
    keep.add(cursor);
    cursor = previous[cursor]!;
  }
  return keep;
}
